import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import askButton from "../../img/ask_button.png";
import PromptExportSheet from "./PromptExportSheet.jsx";
import ModalLawCharge from "../Modal/ModalLawCharge.jsx";
import { sendOfferViewedOnce } from "../../utils/promptExportApi.js";
import useStore from "../../store.js";

/*
'내가 할 일 찾기' 플로팅 도크. 3상태:
1) 대기(작게) — 스크롤 중 조용히 대기
2) 확대 — 스크롤이 멈추면 같은 도크가 부드럽게 확대
3) 준비 — 구매 후 '질문 준비됨 · 다시 열기', 재진입 후 복사(무과금)
결제 전 닫기는 과금 없이 읽던 위치를 유지한다.
*/
const EXPAND_DELAY_MS = 360;

const PromptDock = ({ logId, initialPurchased }) => {
  const [purchasedLogId, setPurchasedLogId] = useState(null);
  const purchased = Boolean(initialPurchased) || purchasedLogId === logId;
  const [expanded, setExpanded] = useState(false);
  const [sheetLogId, setSheetLogId] = useState(null);
  const sheetOpen = sheetLogId === logId;
  const [showCharge, setShowCharge] = useState(false);
  const dockRef = useRef(null);
  // 모바일 질문 내역이 열리면 도크가 아래쪽 메뉴(문의하기·이용약관)를 덮어 탭을 가로채므로 숨긴다.
  const isMobileSidebarOpen = useStore((state) => state.isMobileSidebarOpen);

  useEffect(() => {
    setSheetLogId(null);
  }, [logId]);

  // 판례 5건이 모두 표시된 뒤에만 마운트되므로, 여기서 제안 노출 1회를 기록한다.
  useEffect(() => {
    sendOfferViewedOnce(logId);
  }, [logId]);

  // 추가 등장 대기 없이 작은 도크를 보여준다. 연속 스크롤 중에는 확대를 미룬다.
  useEffect(() => {
    let stopTimer;
    const onScroll = () => {
      setExpanded(false);
      clearTimeout(stopTimer);
      stopTimer = setTimeout(() => setExpanded(true), EXPAND_DELAY_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(stopTimer);
    };
  }, [logId]);

  // 판례 버튼과 추가 질문을 가리면 도크를 해당 영역 위로 옮긴다.
  useEffect(() => {
    const dock = dockRef.current;
    let frame;
    const reposition = () => {
      frame = null;
      const rect = dock.getBoundingClientRect();
      const previous = Number.parseFloat(dock.style.getPropertyValue("--dock-clearance")) || 0;
      const bottom = rect.bottom + previous;
      const top = rect.top + previous;
      const actions = [...document.querySelectorAll('[data-testid="case-card"] button, [data-testid="self-check-section"]')]
        .map((button) => button.getBoundingClientRect())
        .filter((action) => action.width > 0 && action.bottom > 0 && action.top < window.innerHeight && action.left < rect.right && action.right > rect.left)
        .sort((a, b) => b.top - a.top);
      let clearance = 0;
      for (const action of actions) {
        if (action.top < bottom - clearance && action.bottom > top - clearance) {
          clearance = bottom - action.top + 12;
        }
      }
      dock.style.setProperty("--dock-clearance", `${clearance}px`);
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(reposition); };
    const observer = new ResizeObserver(schedule);
    observer.observe(dock);
    document.querySelectorAll('[data-testid="case-card"], [data-testid="self-check-section"]').forEach((card) => observer.observe(card));
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [logId]);

  const openSheet = () => setSheetLogId(logId);

  return (
    <>
      <DockBottomSpace aria-hidden="true" />
      <DockShell
        ref={dockRef}
        type="button"
        data-testid={purchased ? "prompt-dock-done" : expanded ? "prompt-dock-expanded" : "prompt-dock-idle"}
        aria-label={purchased ? "질문 준비됨 · 다시 열기" : "내가 할 일 찾기 · 10로우"}
        aria-haspopup="dialog"
        hidden={isMobileSidebarOpen}
        onClick={openSheet}
      >
        {purchased ? (
          <DonePill>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <defs>
                <linearGradient id="pdDone" x1="0" y1="0" x2="32" y2="32">
                  <stop offset="0.3" stopColor="#00F370" />
                  <stop offset="1" stopColor="#0057FF" />
                </linearGradient>
              </defs>
              <circle cx="16" cy="16" r="15" stroke="url(#pdDone)" strokeWidth="1.5" fill="#fff" />
              <path d="M10 16.5L14 20.5L22 12.5" stroke="url(#pdDone)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <DockText>질문 준비됨 · 다시 열기</DockText>
          </DonePill>
        ) : (
          <ActionPill $expanded={expanded}>
            <ArrowImage src={askButton} alt="" $expanded={expanded} />
            <DockCopy $expanded={expanded}>
              <CopyLine $visible={!expanded} aria-hidden={expanded}>내가 할 일 찾기</CopyLine>
              <CopyLine $visible={expanded} aria-hidden={!expanded}>
                판례는 찾았어요.<br />
                이제 내가 할 일을 알아볼 차례예요.
              </CopyLine>
            </DockCopy>
            <PriceBadge $small={!expanded}>10로우</PriceBadge>
          </ActionPill>
        )}
      </DockShell>

      {sheetOpen && <PromptExportSheet
        key={logId}
        isOpen={sheetOpen}
        onClose={() => setSheetLogId(null)}
        logId={logId}
        purchased={purchased}
        onPurchased={() => setPurchasedLogId(logId)}
        onInsufficientCredit={() => {
          setSheetLogId(null);
          setShowCharge(true);
        }}
      />}
      <ModalLawCharge isOpen={showCharge} onClose={() => setShowCharge(false)} />
    </>
  );
};

const dockEnter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// 마지막 피드백도 확대 도크 위로 스크롤할 수 있도록 공간을 남긴다.
const DockBottomSpace = styled.div`
  height: calc(58px + 32px + 16px + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
`;

const DockShell = styled.button`
  --dock-bottom: 32px;
  position: fixed;
  right: 28px;
  bottom: calc(var(--dock-bottom) + var(--dock-clearance, 0px));
  z-index: 130;
  cursor: pointer;
  display: block;
  padding: 0;
  border: 0;
  border-radius: 30px;
  background: transparent;
  font-family: inherit;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  animation: ${dockEnter} 400ms cubic-bezier(0.22, 1, 0.36, 1);

  &[hidden] {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid #0057ff;
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 480px) {
    right: 16px;
    --dock-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  }
`;

const pillBase = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
`;

// CSS transitions는 진행 중인 크기에서 새 목표로 이어져 빠르게 재스크롤해도 점프하지 않는다.
const morphMotion = css`
  transition-duration: var(--dock-duration);
  transition-timing-function: var(--dock-easing);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ActionPill = styled.span`
  ${pillBase}
  --dock-duration: ${({ $expanded }) => ($expanded ? "560ms" : "420ms")};
  --dock-easing: ${({ $expanded }) => ($expanded
    ? "cubic-bezier(0.22, 1.12, 0.36, 1)"
    : "cubic-bezier(0.22, 0.8, 0.32, 1)")};
  box-sizing: border-box;
  gap: ${({ $expanded }) => ($expanded ? 10 : 8)}px;
  height: ${({ $expanded }) => ($expanded ? 58 : 42)}px;
  padding: ${({ $expanded }) => ($expanded ? "0 8px 0 6px" : "0 6px 0 4px")};
  border-radius: ${({ $expanded }) => ($expanded ? 29 : 21)}px;
  border: 1px solid transparent;
  background-image: linear-gradient(#fff, #fff),
    linear-gradient(90deg, #1ae276 33.51%, #3a7dff 86.88%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: ${({ $expanded }) => ($expanded
    ? "0 4px 24px rgba(0, 243, 112, 0.25)"
    : "0 4px 20px rgba(0, 0, 0, 0.1)")};
  transition-property: height, padding, gap, border-radius, box-shadow;
  ${morphMotion}
`;

const DonePill = styled.span`
  ${pillBase}
  height: 40px;
  padding: 0 14px 0 4px;
  border-radius: 20px;
  border: 0.5px solid #1ae276;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const ArrowImage = styled.img`
  width: ${({ $expanded }) => ($expanded ? 40 : 32)}px;
  height: ${({ $expanded }) => ($expanded ? 40 : 32)}px;
  flex-shrink: 0;
  border-radius: 50%;
  transition-property: width, height;
  ${morphMotion}
`;

const DockText = styled.span`
  color: rgba(0, 0, 0, 0.8);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.26px;
  white-space: nowrap;
`;

const DockCopy = styled.span`
  position: relative;
  width: ${({ $expanded }) => ($expanded ? 13.5 : 5.85)}em;
  height: 36px;
  overflow: hidden;
  font-size: clamp(11px, 3.333vw, 13px);
  color: rgba(0, 0, 0, 0.8);
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.02em;
  transition-property: width;
  ${morphMotion}
`;

const CopyLine = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  white-space: nowrap;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? "-50%" : "calc(-50% + 4px)")});
  transition: opacity 180ms ease, transform 280ms var(--dock-easing);
  transition-delay: ${({ $visible }) => ($visible ? "60ms" : "0ms")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PriceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: ${({ $small }) => ($small ? 28 : 32)}px;
  padding: 0 ${({ $small }) => ($small ? 12 : 14)}px;
  border-radius: ${({ $small }) => ($small ? 14 : 16)}px;
  background: linear-gradient(92deg, #00f370 0%, #00badc 50.5%, #0057ff 100%);
  color: #111111;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.24px;
  white-space: nowrap;
  flex-shrink: 0;
  transition-property: height, padding, border-radius;
  ${morphMotion}
`;

export default PromptDock;
